import React from 'react';

import { Add as AddIcon, BorderColor as BorderColorIcon } from '@mui/icons-material';
import { Button } from '@mui/material';
import { useNavigate } from 'react-router';

import { parseNullableValue } from '@hrlogics/helper-utils';

import { STATUSES_FILTER_OPTIONS } from '@clearverify/constants';

import { CompanyType, type EmployerTableItemFragment } from 'src/api';
import { ClickableLabel } from 'src/components/common';
import {
  Table,
  TableColumn,
  TableExcelColumn,
  TableFilter,
  TableRowAction,
  TableSelectedRowId,
  TableThemes,
  TableTopAction,
} from 'src/components/table';
import { COMPANY_SEARCH_CATEGORIES } from 'src/constants/company';
import type { ModalId } from 'src/constants/modals';
import { useSearch } from 'src/hooks/common';
import { useModal } from 'src/hooks/shared';
import { PaginationContextValue } from 'src/providers/PaginationProvider';
import { getAddressString } from 'src/utils/address';
import { buildEmployerDetailsUrl } from 'src/utils/employer';

type EmployersInnerTablePEOPayrollProviderStaffProps = {
  employersList: EmployerTableItemFragment[];
  employersCount: number;
  loading: boolean;
  inviteModalId?: ModalId;
  isDetail?: boolean;
  tableTheme?: TableThemes;
  pagination: PaginationContextValue;
  onRefresh?: () => Promise<void>;
};

export const EmployersInnerTablePEOPayrollProviderStaff: React.FC<
  EmployersInnerTablePEOPayrollProviderStaffProps
> = ({
  employersList,
  employersCount,
  loading,
  inviteModalId,
  isDetail = false,
  tableTheme = TableThemes.default,
  pagination,
  onRefresh,
}) => {
  const navigate = useNavigate();

  const { openModal } = useModal();

  const { setSearchValue, searchValue, setSearchCategory, searchCategory } = useSearch();

  const [selectedIds, setSelectedIds] = React.useState<TableSelectedRowId[]>([]);

  // biome-ignore lint/correctness/useExhaustiveDependencies: useEffect must not depend on "pagination" here
  React.useEffect(() => {
    pagination.resetPage();
  }, [searchValue, searchCategory]);

  const rowActions = React.useMemo(
    (): Array<TableRowAction<EmployerTableItemFragment>> => [
      {
        startIcon: <BorderColorIcon style={{ margin: '0 10px 0 0' }} />,
        actionName: 'View Details',
        onAction: employer => {
          navigate(buildEmployerDetailsUrl(employer.id));
        },
      },
    ],
    [navigate],
  );

  const topActions = React.useMemo(
    (): Array<TableTopAction> =>
      inviteModalId
        ? [
            {
              actionName: 'Add',
              show: true,
              icon: <AddIcon />,
              onAction: () => openModal(inviteModalId),
              render() {
                return (
                  <Button
                    key={this.actionName}
                    variant="contained"
                    startIcon={this.icon}
                    onClick={this.onAction}
                  >
                    New
                  </Button>
                );
              },
            },
          ]
        : [],
    [inviteModalId, openModal],
  );

  const columns = React.useMemo(
    (): Array<TableColumn<EmployerTableItemFragment>> => [
      {
        title: 'Name',
        key: 'displayName',
        sortable: true,
        render: (_valueByDataPath, employer) => {
          return (
            <ClickableLabel
              type="link"
              to={buildEmployerDetailsUrl(employer.id)}
              label={employer.displayName}
              logoUrl={employer.logo?.downloadUrl}
              withoutLogo={isDetail}
            />
          );
        },
      },
      {
        title: 'PEO',
        key: 'peo.displayName',
        sortable: true,
        render: (_valueByDataPath, employer) => {
          return employer.controlledBy === CompanyType.Employer ? '–' : employer.peo?.displayName;
        },
      },
      {
        title: 'Address',
        key: 'headquarters',
        render: (_valueByDataPath, employer) => {
          return parseNullableValue(getAddressString(employer.headquarters));
        },
      },
    ],
    [isDetail],
  );

  return (
    <Table
      dataSource={employersList}
      rowCount={employersCount}
      columns={columns}
      rowActions={rowActions}
      topActions={topActions}
      onSearch={setSearchValue}
      onSearchCategorySelect={setSearchCategory}
      pagination={pagination}
      filters={filters}
      selectedIds={selectedIds}
      setSelectedIds={setSelectedIds}
      excelColumns={excelColumns}
      loading={loading}
      showDownloadIcon={false}
      tableTheme={tableTheme}
      onRefresh={onRefresh}
      searchCategories={COMPANY_SEARCH_CATEGORIES}
    />
  );
};

const excelColumns: Array<TableExcelColumn<EmployerTableItemFragment>> = [
  {
    header: 'Name',
    key: 'displayName',
  },
  {
    header: 'PEO',
    key: 'peo',
    render: (_valueByKey, employer) => {
      return employer.controlledBy === CompanyType.Employer ? '–' : employer.peo?.displayName;
    },
  },
  {
    header: 'Address',
    key: 'headquarters',
    render: (_valueByKey, employer) => {
      return parseNullableValue(getAddressString(employer.headquarters));
    },
  },
];

const filters: TableFilter[] = [
  {
    id: 'status',
    type: 'select',
    fieldProps: {
      label: 'Status',
      multiple: true,
      options: STATUSES_FILTER_OPTIONS,
    },
  },
];
